import { getProgressTextColors } from "../textColors";
import React, { useEffect, useRef } from "react";
import { useMoodleV2 } from "../../../contexts/moodle";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/config";
import { requestAutoLogin } from "../../../services/moodle/userauth";
import { getCourseCertificate } from "../../../services/moodle/courses";
import * as d3 from "d3";

// cambio
export const ProgressRowItem = ({ course, grade, progress, lang, includeEncuesta, certificateCmid, rowIndex, onRowClick }) => {
  const moodlev2 = useMoodleV2();
  const theme = useTheme();
  const textColors = getProgressTextColors(theme.mode);
  const currentRequestId = useRef(0);

  const [data /* setData */] = React.useState({
    name: course.displayname,
    start: new Date(course.startdate * 1000).toLocaleDateString(),
    end: course.enddate !== 0 ? new Date(course.enddate * 1000).toLocaleDateString() : "Sin fecha de termino",
  });
  const { user } = useAuth();
  const { config } = useConfig();

  const [downloadStatus, setDownloadStatus] = React.useState("idle"); // "idle" | "loading" | "success"
  const successTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const isEven = rowIndex % 2 === 1;

  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || progress === undefined || progress === null) return;
    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const size = 36;
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const arc = d3.arc().innerRadius(radius - strokeWidth).outerRadius(radius);
    const pie = d3.pie().value((d) => d.value).sort(null);
    const data = [
      { label: "done", value: progress },
      { label: "pending", value: 100 - progress },
    ];
    const colors = d3.scaleOrdinal([
      theme.mode === "dark"
        ? theme.colors.backgroundPendienteLight
        : theme.colors.backgroundPendienteDark,
      theme.mode === "dark"
        ? theme.colors.backgroundCompletadoLight
        : theme.colors.backgroundCompletadoDark,
    ]);

    svg
      .attr("width", size)
      .attr("height", size)
      .append("g")
      .attr("transform", `translate(${size / 2},${size / 2})`)
      .selectAll("path")
      .data(pie(data))
      .join("path")
      .attr("d", arc)
      .attr("fill", (_, i) => colors(i));
  }, [progress, theme.mode, theme.colors]);

  const handleRowClick = () => {
    if (onRowClick) onRowClick(course);
  };

  const handleCourseNavigation = async (e) => {
    e.stopPropagation();
    if (!certificateCmid || downloadStatus !== "idle" || moodlev2.isDownloading) return;

    setDownloadStatus("loading");
    moodlev2.setIsDownloading(true);

    try {
      const res = await getCourseCertificate({
        token: user.token,
        cmid: certificateCmid,
        userid: user.id || 0,
        baseUrl: config.baseURL,
      });

      if (res && res.filecontent) {
        const byteCharacters = atob(res.filecontent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = res.filename || `Certificado_${course.displayname || "curso"}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);

        setDownloadStatus("success");
        successTimerRef.current = setTimeout(() => {
          setDownloadStatus("idle");
        }, 2000);
        return;
      }
    } catch (err) {
      console.warn("Direct certificate download not available or failed, using fallback:", err.message);
      try {
        let { key } = await requestAutoLogin({
          username: user.username,
          baseUrl: config.baseURL,
          tipddytoken: config.token,
        });
        const url = `https://api-interfaz-alumnos.tipddy.cl/history/cert/${certificateCmid}?userid=${user.id}&domain=${encodeURIComponent(config.baseURL)}&userkey=${key}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (fallbackErr) {
        console.error("Fallback download failed:", fallbackErr);
      }
      setDownloadStatus("idle");
    } finally {
      moodlev2.setIsDownloading(false);
    }
  };

  const [isHovered, setIsHovered] = React.useState(false);
  const isDark = theme.mode === "dark";
  const borderColor = isHovered
    ? isDark ? "rgba(236, 178, 54, 0.45)" : "rgba(236, 178, 54, 0.6)"
    : isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.08)";
  const cellBg = isHovered
    ? isDark ? "rgba(255, 255, 255, 0.05)" : "#F8FAFC"
    : isDark ? "rgba(255, 255, 255, 0.02)" : "#FFFFFF";

  return (
    <>
      <tr
        onClick={handleRowClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="cursor-pointer transition-colors duration-150"
      >
        <th
          scope="row"
          className="px-4 py-3.5 font-montserrat font-semibold text-[max(1rem,16px)] transition-colors rounded-l-md"
          style={{
            backgroundColor: cellBg,
            borderLeft: `1px solid ${borderColor}`,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            color: textColors.primary,
          }}
        >
          <p
            className="font-montserrat font-semibold text-[max(1rem,16px)] break-words whitespace-normal leading-snug"
            style={{ color: textColors.primary }}
          >
            {data.name}
          </p>
        </th>
        <td
          className="px-4 py-3.5 font-montserrat text-[max(1rem,16px)] whitespace-nowrap transition-colors"
          style={{
            backgroundColor: cellBg,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            color: textColors.primary,
          }}
        >
          {progress !== undefined && progress !== null ? (
            <div className="flex items-center gap-2.5">
              <svg ref={chartRef} />
              <span className="font-bold text-[max(1rem,16px)]">{Math.floor(progress)}%</span>
            </div>
          ) : "-"}
        </td>
        <td
          className="px-4 py-3.5 font-montserrat text-[max(1rem,16px)] whitespace-nowrap transition-colors"
          style={{
            backgroundColor: cellBg,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            color: textColors.primary,
          }}
        >
          {grade !== "-" && progress !== null && progress !== undefined && progress < 100 ? (
            <div className="flex flex-col items-start gap-1">
              <span className="font-bold text-[max(1rem,16px)]">{grade}</span>
              <span
                className="text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full font-montserrat"
                style={{
                  backgroundColor: "rgba(236, 178, 54, 0.12)",
                  color: "#ecb236",
                  border: "1px solid rgba(236, 178, 54, 0.35)",
                }}
              >
                En progreso
              </span>
            </div>
          ) : (
            <span className="font-medium">{grade}</span>
          )}
        </td>
        <td
          className="px-4 py-3.5 font-montserrat text-[max(1rem,16px)] whitespace-nowrap transition-colors"
          style={{
            backgroundColor: cellBg,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            color: textColors.primary,
          }}
        >
          {data.start}
        </td>
        <td
          className="px-4 py-3.5 font-montserrat text-[max(1rem,16px)] whitespace-nowrap transition-colors"
          style={{
            backgroundColor: cellBg,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            color: textColors.primary,
          }}
        >
          {data.end === "Sin fecha de termino" ? lang["History"].fourth_column_answer : data.end}
        </td>
        <td
          className={`px-4 py-3.5 font-montserrat text-[max(1rem,16px)] whitespace-nowrap z-50 transition-colors ${!includeEncuesta ? "rounded-r-md" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            backgroundColor: cellBg,
            borderTop: `1px solid ${borderColor}`,
            borderBottom: `1px solid ${borderColor}`,
            borderRight: !includeEncuesta ? `1px solid ${borderColor}` : undefined,
          }}
        >
          {certificateCmid ? (
            <button
              type="button"
              disabled={downloadStatus !== "idle" || moodlev2.isDownloading}
              onClick={(e) => {
                e.stopPropagation();
                if (downloadStatus === "idle" && !moodlev2.isDownloading) {
                  handleCourseNavigation(e);
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[max(1rem,16px)] font-bold font-montserrat transition-all duration-150 select-none ${
                downloadStatus === "loading"
                  ? "cursor-wait opacity-80"
                  : downloadStatus === "success"
                  ? "cursor-default"
                  : "cursor-pointer hover:shadow-sm hover:brightness-105 active:scale-95"
              }`}
              style={{
                backgroundColor:
                  downloadStatus === "success"
                    ? isDark
                      ? "rgba(34, 197, 94, 0.25)"
                      : "#BBF7D0"
                    : isDark
                    ? "rgba(236, 178, 54, 0.15)"
                    : "#FEF3C7",
                color:
                  downloadStatus === "success"
                    ? isDark
                      ? "#4ADE80"
                      : "#15803D"
                    : isDark
                    ? "#ecb236"
                    : "#B45309",
                border:
                  downloadStatus === "success"
                    ? isDark
                      ? "1px solid rgba(74, 222, 128, 0.4)"
                      : "1px solid #BBF7D0"
                    : isDark
                    ? "1px solid rgba(236, 178, 54, 0.4)"
                    : "1px solid #FCD34D",
              }}
            >
              {downloadStatus === "loading" && (
                <>
                  <CircularProgress
                    size={14}
                    thickness={5}
                    sx={{ color: isDark ? "#4ADE80" : "#15803D" }}
                  />
                  <span>Generando...</span>
                </>
              )}
              {downloadStatus === "success" && (
                <>
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: isDark ? "#4ADE80" : "#15803D", fontSize: "16px" }}
                  />
                  <span>¡Descargado!</span>
                </>
              )}
              {downloadStatus === "idle" && (
                <>
                  <DownloadIcon fontSize="small" sx={{ fontSize: "16px" }} />
                  <span>{lang["History"].fifth_column_answer}</span>
                </>
              )}
            </button>
          ) : (
            <Tooltip
              title={
                progress !== null && progress !== undefined && Number(progress) >= 100
                  ? "Este curso no cuenta con certificado emitido"
                  : "Disponible al completar el 100% del curso"
              }
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: "max(1rem, 16px)",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 500,
                    padding: "8px 14px",
                    borderRadius: "6px",
                    lineHeight: 1.4,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
                  },
                },
                arrow: {
                  sx: {
                    fontSize: "14px",
                  },
                },
              }}
            >
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[max(1rem,16px)] font-bold font-montserrat cursor-help transition-all hover:opacity-80 select-none"
                style={{
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "#F3F4F6",
                  color: textColors.secondary,
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.18)" : "1px solid #E5E7EB",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: "14px" }} />
                <span>Pendiente</span>
              </span>
            </Tooltip>
          )}
        </td>
        {includeEncuesta && (
          <td
            className="px-4 py-3.5 font-montserrat text-[max(1rem,16px)] whitespace-nowrap transition-colors rounded-r-md"
            style={{
              backgroundColor: cellBg,
              borderTop: `1px solid ${borderColor}`,
              borderBottom: `1px solid ${borderColor}`,
              borderRight: `1px solid ${borderColor}`,
              color: textColors.primary,
            }}
          >
            -
          </td>
        )}
      </tr>
    </>
  );
};
